"use client";

import { useActionState } from "react";
import { useFormStatus } from "react-dom";
import { enviarContacto, type ContactoState } from "@/app/actions/contacto";
import { useTranslations } from "@/i18n/client";
import { identity, telHref, waHref } from "@/content/identity";

function SubmitButton({ label }: { label: string }) {
  const { pending } = useFormStatus();
  return (
    <button
      type="submit"
      disabled={pending}
      className="w-full mt-2 px-6 py-[18px] bg-[var(--color-accent)] text-[var(--color-bg)] border-0 font-mono text-[11px] tracking-[0.2em] uppercase cursor-pointer rounded-full hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {pending ? "…" : label}
    </button>
  );
}

const initialState: ContactoState = { status: "idle" };

export function ContactoFormPage() {
  const [state, dispatch] = useActionState(enviarContacto, initialState);
  const { t } = useTranslations();
  const contacto = t('contacto');

  const fields = contacto.fields
    ? [
        { label: contacto.fields.nombre.label, placeholder: contacto.fields.nombre.placeholder, name: "nombre", type: "text" },
        { label: contacto.fields.email.label, placeholder: contacto.fields.email.placeholder, name: "email", type: "email" },
        { label: contacto.fields.telefono.label, placeholder: contacto.fields.telefono.placeholder, name: "telefono", type: "tel" },
      ]
    : [
        { label: "Nombre *", placeholder: "Tu nombre", name: "nombre", type: "text" },
        { label: "Email *", placeholder: "tú@empresa.com", name: "email", type: "email" },
        { label: "Teléfono (opcional)", placeholder: "+34 ...", name: "telefono", type: "tel" },
      ];

  const mensajeLabel = contacto.fields?.mensaje.label || "Cuéntanos tu proyecto *";
  const mensajePlaceholder = contacto.fields?.mensaje.placeholder || "Qué haces, a quién vendes, qué necesitas…";
  const submitLabel = contacto.submit || "Quiero mi web →";

  return (
    <section className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 py-[80px] lg:py-[120px]">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20 items-start">

        {/* LEFT — info + proceso */}
        <div className="space-y-6">
          {/* Contact card */}
          <div className="bg-[color:var(--color-bg)] border border-[color:var(--color-border)] rounded-md p-8">
            <div className="text-[11px] font-mono tracking-widest uppercase text-[var(--color-mute)] mb-6">
              {contacto.overline || 'Contáctanos'}
            </div>
            <div className="space-y-4 font-mono text-[13px] tracking-[0.08em]">
              {identity.telefonos.map((tel) => (
                <div key={tel} className="space-y-3 pt-2">
                  <div className="text-[10px] text-[var(--color-mute)] mb-1">{tel}</div>
                  <div className="flex flex-wrap gap-4">
                    <a
                      href={waHref(tel)}
                      className="flex items-center gap-2 text-[color:var(--color-ink)] no-underline hover:text-[color:var(--color-accent)] transition-colors group"
                    >
                      <span className="w-6 h-6 flex items-center justify-center bg-[var(--color-accent-soft)] rounded-full text-[var(--color-accent)] group-hover:bg-[var(--color-accent)] group-hover:text-[var(--color-bg)] transition-all">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
                      </span>
                      WhatsApp
                    </a>
                    <a
                      href={telHref(tel)}
                      className="flex items-center gap-2 text-[color:var(--color-ink)] no-underline hover:text-[color:var(--color-accent)] transition-colors group"
                    >
                      <span className="w-6 h-6 flex items-center justify-center bg-[var(--color-border)] rounded-full text-[var(--color-mute)] group-hover:bg-[var(--color-ink)] group-hover:text-white transition-all">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                      </span>
                      Llamar
                    </a>
                  </div>
                </div>
              ))}
              <a
                href={`mailto:${identity.email}`}
                className="flex items-center gap-3 text-[color:var(--color-ink)] no-underline hover:text-[color:var(--color-accent)] transition-colors"
              >
                <span className="text-[color:var(--color-accent)]">▶</span>
                {identity.email}
              </a>
              <div className="flex items-center gap-3 text-[color:var(--color-mute)]">
                <span className="text-[color:var(--color-accent)]">▶</span>
                {identity.ubicacion}
              </div>
            </div>
          </div>

          {/* Proceso */}
          <div className="bg-[color:var(--color-surface)] border border-[color:var(--color-border)] rounded-md p-8">
            <div className="text-[11px] font-mono tracking-widest uppercase text-[var(--color-mute)] mb-6">
              {contacto.overline || 'Cómo funciona'}
            </div>
            <div className="space-y-6">
              {[
                { num: "01", title: contacto.fields ? "Cuéntanos tu proyecto" : "Tell us your project", desc: "Hoy mismo." },
                { num: "02", title: "Maqueta en 72 horas", desc: "Ves el resultado antes de comprometerte." },
                { num: "03", title: "Si no te encanta, no pagas", desc: "Riesgo cero, garantizado." },
              ].map((step) => (
                <div key={step.num} className="flex gap-5 items-start">

                  <div>
                    <div className="font-sans font-[350] text-[18px] text-[color:var(--color-ink)] leading-[1.2]">
                      {step.title}
                    </div>
                    <div className="font-sans text-[13px] text-[color:var(--color-mute)] mt-1">
                      {step.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT — form */}
        <div className="bg-[color:var(--color-surface)] border border-[color:var(--color-border)] rounded-md p-9">
          {state.status === "success" ? (
            <div className="text-center py-12">
              <div className="text-[48px] mb-6" aria-hidden="true">✓</div>
              <h2 className="font-sans font-[350] text-[32px] text-[color:var(--color-ink)] mb-4">
                ¡Mensaje recibido!
              </h2>
              <p className="font-sans text-[16px] text-[color:var(--color-mute)] leading-[1.6] max-w-[360px] mx-auto">
                {state.message} También te hemos enviado un email de confirmación.
              </p>
              <a
                href={waHref(identity.telefonos[0])}
                className="inline-flex mt-8 px-6 py-4 bg-[var(--color-accent)] text-[var(--color-bg)] font-mono text-[11px] tracking-[0.2em] uppercase rounded-full no-underline hover:opacity-90 transition-opacity"

              >
                WhatsApp directo →
              </a>
            </div>
          ) : (
            <form action={dispatch} className="grid gap-5" noValidate>
              <div className="text-[11px] font-mono tracking-widest uppercase text-[var(--color-mute)] mb-1">
                {contacto.overline || 'Tu proyecto'}
              </div>

              {state.status === "error" && (
                <div
                  role="alert"
                  className="font-sans text-[14px] text-[color:var(--color-accent)] bg-[color:var(--color-accent-soft)] px-4 py-3 rounded"
                >
                  {state.message}
                </div>
              )}

              {fields.map(({ label, placeholder, name, type }) => (
                <label key={name} className="block">
                  <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-[color:var(--color-mute)] mb-2">
                    {label}
                  </div>
                  <input
                    name={name}
                    type={type}
                    placeholder={placeholder}
                    className="w-full py-3.5 px-0 bg-transparent border-0 border-b border-[color:var(--color-border)] font-sans text-[18px] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] transition-colors"
                  />
                </label>
              ))}

              <label className="block">
                <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-[color:var(--color-mute)] mb-2">
                  {mensajeLabel}
                </div>
                <textarea
                  name="mensaje"
                  rows={4}
                  placeholder={mensajePlaceholder}
                  className="w-full py-3.5 px-0 bg-transparent border-0 border-b border-[color:var(--color-border)] font-sans text-[17px] text-[color:var(--color-ink)] outline-none resize-none focus:border-[color:var(--color-accent)] transition-colors"
                />
              </label>

              <div className="flex items-start gap-3 mt-2">
                <input
                  type="checkbox"
                  name="legal"
                  id="legal"
                  required
                  className="mt-1 w-4 h-4 rounded border-[color:var(--color-border)] bg-transparent text-[var(--color-accent)] focus:ring-[var(--color-accent)] transition-colors cursor-pointer"
                />
                <label 
                  htmlFor="legal" 
                  className="font-sans text-[13px] text-[color:var(--color-mute)] leading-[1.4] cursor-pointer"
                  dangerouslySetInnerHTML={{ __html: contacto.fields?.legal || 'Acepto la política de privacidad y los términos y condiciones.' }}
                />
              </div>

              <SubmitButton label={submitLabel} />

              <p className="font-mono text-[10px] tracking-[0.12em] text-[color:var(--color-mute)] text-center leading-[1.8]">
                {contacto.subtitle || 'Sin pago inicial · Maqueta en 72 h · Si no te encanta, no pagas'}
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
